/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Component, inject } from "@angular/core";
import {
    ButtonComponent,
    DatetimePipe,
    DeleteResourceSectionComponent,
    IntegrationCardComponent,
    PropertiesTableComponent,
    PropertiesTableRowComponent
} from "typedb-cloud-framework";

import { ApplicationState } from "../../../../service/application-state.service";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RouterLink } from "@angular/router";
import { GaugeModule } from "angular-gauge";
import { SecurityRecommendations } from "../../../../concept/security-recommendation";
import { TeamApi } from "../../../../service/team/team-api.service";
import { BehaviorSubject, map, Observable, ReplaySubject, startWith } from "rxjs";
import { AsyncPipe } from "@angular/common";
import { toSentenceCase } from "../../../../util";
import { MatExpansionModule } from "@angular/material/expansion";

@Component({
    selector: "tp-security-center-overview",
    templateUrl: "./security-center-overview.component.html",
    styleUrls: ["./security-center-overview.component.scss", "../security-center.scss"],
    imports: [
        RouterLink, MatTooltipModule, GaugeModule, AsyncPipe, MatExpansionModule
    ]
})
export class SecurityCenterOverviewComponent {
    private app = inject(ApplicationState);
    private teamApi = inject(TeamApi);

    readonly team = this.app.requireCurrentTeam();

    securityRecommendations$ = new ReplaySubject<SecurityRecommendations>(1);
    securityScore$ = this.securityRecommendations$.pipe(map(x => this.computeSecurityScore(x)));
    securityLevel$: Observable<"low" | "medium" | "high"> = this.securityScore$.pipe(
        map(x => {
            if (x <= 35) return "low";
            else if (x <= 75) return "medium";
            else return "high";
        })
    );
    displayedValue$ = new BehaviorSubject<number>(0);
    scoreColor$ = this.securityScore$.pipe(
        map(this.computeColorFromSecurityScore),
        startWith("rgb(233, 100, 100)"),
    );

    constructor() {
        this.teamApi.getTeamSecurityRecommendations(this.team.uuid).subscribe(x => this.securityRecommendations$.next(x));
        setTimeout(() => this.securityScore$.subscribe(x => this.displayedValue$.next(x)), 0);
    }

    computeColorFromSecurityScore(securityScore: number) {
        if (securityScore <= 20) return "#e96464";
        else if (securityScore <= 40) return "#f5a65c";
        else if (securityScore <= 60) return "#fdd257";
        else if (securityScore <= 80)    return "#66d79b";
        else return "#7ba0ff";
    }

    private computeSecurityScore(recommendations: SecurityRecommendations): number {
        let score = 100;
        if (recommendations.addSecurityContact) score -= 20;
        if (recommendations.reduceAdminCount) score -= 20;
        if (recommendations.increaseMFAPolicy) {
            if (recommendations.increaseMFAPolicy.mfaPolicy === "none") score -= 20;
            else if (recommendations.increaseMFAPolicy.mfaPolicy === "encouraged") score -= 10;
        }
        if (recommendations.enableMFA) score -= 20;
        if (recommendations.setBackups) score -= 20;
        return score;
    }

    protected readonly toSentenceCase = toSentenceCase;
    protected readonly PI = Math.PI;
}
